import React from "react";
import { Link } from "react-router-dom";

/**
 * Compares two semver strings (e.g. "1.0.6" vs "1.0.7").
 * Returns true if `installed` is strictly older than `latest`.
 */
export function isPSSOutdated(installed, latest) {
  if (!installed || !latest) return false;
  const parse = (v) => String(v).split(".").map((n) => parseInt(n, 10) || 0);
  const a = parse(installed);
  const b = parse(latest);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av < bv) return true;
    if (av > bv) return false;
  }
  return false;
}

/**
 * Shows a prominent warning banner when the installed PSS version is outdated.
 * Renders nothing when versions are in sync or when PSS is not running.
 *
 * Props:
 *   installedVersion  – version string from PSS /ping (e.g. "1.0.6"), or null
 *   latestVersion     – version string from PP API (e.g. "1.0.7"), or null
 *   style             – optional extra style for the outer wrapper
 */
const PSSUpdateBanner = ({ installedVersion, latestVersion, style }) => {
  if (!isPSSOutdated(installedVersion, latestVersion)) return null;

  return (
    <div style={{ ...bannerStyle, ...style }}>
      <span style={iconStyle}>⚠️</span>
      <div style={bodyStyle}>
        <strong style={titleStyle}>PSA Studio update required</strong>
        <span style={msgStyle}>
          You have <strong>v{installedVersion}</strong> installed but the latest is{" "}
          <strong>v{latestVersion}</strong>. Please update Studio before uploading to ensure
          seamless uploads and full compatibility.
        </span>
      </div>
      <Link to="/companion" style={btnStyle}>
        Update Studio →
      </Link>
    </div>
  );
};

const bannerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  background: "rgba(251,191,36,0.13)",
  border: "1px solid rgba(251,191,36,0.45)",
  borderRadius: 12,
  padding: "14px 18px",
  marginBottom: 20,
  flexWrap: "wrap",
};

const iconStyle = { fontSize: 22, flexShrink: 0 };

const bodyStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 3,
  minWidth: 200,
};

const titleStyle = {
  fontSize: 13.5,
  fontWeight: 700,
  color: "#92400e",
};

const msgStyle = {
  fontSize: 13,
  color: "#78350f",
  lineHeight: 1.55,
};

const btnStyle = {
  flexShrink: 0,
  background: "linear-gradient(135deg,#d97706,#b45309)",
  color: "#fff",
  padding: "9px 18px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

export default PSSUpdateBanner;
